export const createUnloadArtifactFromMemory = (deps) => {
    return (artifactId, artifact) => {
        const { viewerRef, artifactActions, addStatus } = deps;
        const currentViewer = viewerRef.current;
        if (!currentViewer)
            return;
        // Deliberately no blob URL in the cache: it is revoked below, so keeping it
        // would only hand a reload a dead handle. Reloads re-fetch from the server.
        artifactActions.updateMetadataCache(artifactId, {
            metadata: artifact.metadata,
            loadParams: {
                type: artifact.type,
            },
            lastUnloadTime: Date.now()
        });
        try {
            if (artifact.type === 'raster') {
                currentViewer.imageryLayers.remove(artifact.cesiumObject);
            }
            else if (artifact.type === 'vector') {
                currentViewer.dataSources.remove(artifact.cesiumObject);
            }
            else if (artifact.type === 'placemark') {
                currentViewer.entities.remove(artifact.cesiumObject);
            }
            else if (artifact.type === 'screen') {
                artifactActions.removeScreenOverlay(artifactId);
            }
            else if (artifact.type === 'info') {
                artifactActions.removeInfoPanel(artifactId);
            }
            if (artifact.blobUrl) {
                URL.revokeObjectURL(artifact.blobUrl);
            }
            artifactActions.removeLoaded(artifactId);
            addStatus(`Unloaded ${artifact.metadata.getName()} to save memory`);
        }
        catch (error) {
            console.error('Error unloading artifact:', error);
            addStatus(`Error unloading ${artifact.metadata.getName()}: ${error}`);
        }
    };
};
export const createReloadArtifact = (deps) => {
    return async (artifactId, shouldSetVisible = true) => {
        const { artifactState, artifactActions, addStatus, loadArtifact } = deps;
        const cachedData = artifactActions.getMetadataCache(artifactId);
        if (!cachedData) {
            console.error(`No cached metadata found for artifact: ${artifactId}`);
            addStatus(`Cannot reload artifact - no cached data found`);
            return false;
        }
        if (!cachedData.metadata) {
            console.error(`Cached data for artifact ${artifactId} is missing metadata`);
            addStatus(`Cannot reload artifact - cached data is invalid`);
            return false;
        }
        artifactActions.setLoading(artifactId, true);
        artifactActions.updateVisibility(artifactId, false);
        try {
            await new Promise((resolve, reject) => {
                const timeoutId = setTimeout(() => {
                    reject(new Error('Reload timeout'));
                }, 30000);
                loadArtifact(artifactId, cachedData.metadata, (id, data, metadata) => {
                    clearTimeout(timeoutId);
                    resolve();
                });
            });
            if (shouldSetVisible) {
                artifactActions.updateVisibility(artifactId, true);
                const reloadedArtifact = artifactState.loaded.get(artifactId);
                if (reloadedArtifact && reloadedArtifact.cesiumObject) {
                    if (reloadedArtifact.type === 'raster' || reloadedArtifact.type === 'vector' || reloadedArtifact.type === 'placemark') {
                        reloadedArtifact.cesiumObject.show = true;
                    }
                }
            }
            addStatus(`Reloaded ${cachedData.metadata.getName()} from server`);
            return true;
        }
        catch (error) {
            console.error('Error reloading artifact:', error);
            addStatus(`Failed to reload ${cachedData.metadata.getName()}: ${error}`);
            artifactActions.updateVisibility(artifactId, false);
            return false;
        }
        finally {
            artifactActions.setLoading(artifactId, false);
        }
    };
};
export const createClearAll = (deps) => {
    return () => {
        const { viewerRef, artifactState, artifactActions } = deps;
        const currentViewer = viewerRef.current;
        if (!currentViewer)
            return;
        artifactState.loaded.forEach((artifact) => {
            if (artifact.type === 'raster') {
                currentViewer.imageryLayers.remove(artifact.cesiumObject);
            }
            else if (artifact.type === 'vector') {
                currentViewer.dataSources.remove(artifact.cesiumObject);
            }
            else if (artifact.type === 'placemark') {
                currentViewer.entities.remove(artifact.cesiumObject);
            }
            if (artifact.blobUrl) {
                URL.revokeObjectURL(artifact.blobUrl);
            }
        });
        artifactActions.clearAll();
    };
};
